import Cookies from 'universal-cookie';

const cookies = new Cookies();

const APP_LABELS = {
  abs: 'ABS App',
  tl: 'TimeLite App',
  eo: 'Exkursio App',
};

let installedCallback = null;
let tokenOnlyRequest = null;

function getNativePlatform() {
  const platform = cookies.get('abs_nfcsupport');
  return platform === 'android' || platform === 'ios' ? platform : null;
}

function getNativeAppLabel() {
  const appCookie = cookies.get('abs_nativeapp');
  return APP_LABELS[appCookie] ?? 'ABS Web';
}

function decodeBase64Utf8(encodedPayload) {
  const binary = window.atob(encodedPayload);
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}

function completeTokenOnlyRequest(token) {
  if (!tokenOnlyRequest) return false;

  const { resolve, timeoutId } = tokenOnlyRequest;
  clearTimeout(timeoutId);
  tokenOnlyRequest = null;
  resolve(token || null);
  return true;
}

export function isNativePushAvailable() {
  if (typeof window === 'undefined') return false;
  return typeof window.callAppHook === 'function' && getNativePlatform() != null;
}

export function installNativePushBridge() {
  if (!isNativePushAvailable()) return false;

  const callback = async (encodedPayload) => {
    try {
      const platform = getNativePlatform();
      if (!platform || typeof window.callAppHook !== 'function') return;

      const decodedPayload = decodeBase64Utf8(encodedPayload);
      const pushDeviceInfo = JSON.parse(decodedPayload);
      const token = typeof pushDeviceInfo?.token === 'string' ? pushDeviceInfo.token.trim() : '';
      if (!token) {
        completeTokenOnlyRequest(null);
        return;
      }

      if (completeTokenOnlyRequest(token)) return;

      const app = getNativeAppLabel();
      const payload = {
        token,
        old_token: pushDeviceInfo?.tokenOld || null,
        platform,
        app,
        device_name: pushDeviceInfo?.deviceName || app,
      };

      const { default: absWebService } = await import('./absWebService');
      const response = await absWebService.communicationHandler('abs_register_push_device', payload);
      if (absWebService.isResponseArrSuccess(response) && typeof window.callAppHook === 'function') {
        window.callAppHook(`confirmpushtoken###${token}`);
      }
    } catch (error) {
      completeTokenOnlyRequest(null);
      // Native keeps the token pending and retries on a later app load.
    }
  };

  installedCallback = callback;
  window.processPushDeviceInformation = callback;
  return true;
}

export function requestNativePushToken() {
  if (!isNativePushAvailable()) return false;
  window.callAppHook('getpushtoken');
  return true;
}

export function requestNativePushTokenOnly(timeout = 3000) {
  if (!isNativePushAvailable()) return Promise.resolve(null);

  if (installedCallback == null || window.processPushDeviceInformation !== installedCallback) {
    installNativePushBridge();
  }

  if (installedCallback == null || window.processPushDeviceInformation !== installedCallback) {
    return Promise.resolve(null);
  }

  if (tokenOnlyRequest?.timeoutId) {
    clearTimeout(tokenOnlyRequest.timeoutId);
    tokenOnlyRequest.resolve(null);
  }

  return new Promise(resolve => {
    tokenOnlyRequest = {
      resolve,
      timeoutId: setTimeout(() => {
        tokenOnlyRequest = null;
        resolve(null);
      }, timeout),
    };

    try {
      window.callAppHook('getpushtoken');
    } catch (error) {
      completeTokenOnlyRequest(null);
    }
  });
}

export function cleanupNativePushBridge() {
  if (typeof window === 'undefined') return;
  if (installedCallback != null && window.processPushDeviceInformation === installedCallback) {
    delete window.processPushDeviceInformation;
  }
  installedCallback = null;
  completeTokenOnlyRequest(null);
}
